/**
 * Ajustes — MOBILE: secciones apiladas, controles de tamaño de dedo.
 *
 * Acá vive el interruptor de la regla 1 del proyecto: forzar la interfaz de escritorio en el
 * celular, o dejarla en automático.
 *
 * Y acá vive "Rutina y plan", la sección que toda la app nombraba y no existía: hasta ahora la
 * rutina, el gimnasio y el paquete se definían una sola vez, en la bienvenida. Va plegada porque
 * en 390 px un editor de rutina abierto empuja todo lo demás fuera de la pantalla; se abre sola
 * cuando se llega con `ir('ajustes', 'rutina')`.
 */

import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { fechaLarga, type Ejercicio, type Sexo, type Unidad } from '@mg/core';

import { objetivoOpcional, useAjustes } from '../features/useAjustes.ts';
import { MINIMO_CLAVE, useClave } from '../features/useClave.ts';
import { useRutina, type DiaDeRutina } from '../features/useRutina.ts';
import { useEstadoSync } from '../ui/IndicadorSync.tsx';
import { useVariante } from '../ui/layout-variant.tsx';
import { HojaInferior } from '../ui/Shell.mobile.tsx';
import { Aviso, Campo, Icono, Insignia, Selector, Vacio } from '../ui/componentes.tsx';
import { PanelDeAyuda, reiniciarPistas } from '../ui/Pista.tsx';
import { useRuta } from '../ui/router.tsx';

export function AjustesMobile({ onSalir }: { onSalir: () => Promise<void> }): ReactNode {
  const a = useAjustes();
  const { detalle } = useRuta();
  const { variante, preferencia, fijar, detectada } = useVariante();
  const sync = useEstadoSync();
  const [altura, setAltura] = useState(() => String(a.datos.perfil?.altura_cm ?? ''));

  const refRutina = useRef<HTMLElement | null>(null);
  const refObjetivos = useRef<HTMLElement | null>(null);
  const listo = !a.datos.cargando && a.datos.perfil !== null;

  // El ancla de navegación: los textos que dicen "se edita en Ajustes" son botones que llegan
  // hasta acá, y tienen que dejar al usuario mirando la sección, no el principio de la pantalla.
  useEffect(() => {
    if (!listo) return;
    const destino =
      detalle === 'rutina' ? refRutina.current : detalle === 'objetivos' ? refObjetivos.current : null;
    destino?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [detalle, listo]);

  if (a.datos.cargando || !a.datos.perfil) return <Vacio>Cargando…</Vacio>;
  const p = a.datos.perfil;

  return (
    <>
      <section className="tarjeta">
        <span className="eyebrow">Perfil</span>
        <div className="columna" style={{ gap: 'var(--e3)', marginTop: 'var(--e3)' }}>
          <div>
            <Campo
              etiqueta="Nombre"
              texto
              defaultValue={p.nombre}
              onBlur={(e) => void a.guardar({ nombre: e.target.value }, 'nombre')}
            />
            <MarcaGuardado visible={a.guardado === 'nombre'} />
          </div>

          <div>
            <Campo
              etiqueta="Altura (cm)"
              inputMode="numeric"
              value={altura}
              onChange={(e) => setAltura(e.target.value)}
              onBlur={() => {
                const n = Number(altura);
                void a.guardar({ altura_cm: Number.isFinite(n) && n > 0 ? n : null }, 'altura');
              }}
              ayuda="Necesaria para el FFMI. Se pide una sola vez."
            />
            <MarcaGuardado visible={a.guardado === 'altura'} />
          </div>

          {a.alturaSugerida && (
            <Aviso tono="info">
              De tu IMC sale {a.alturaSugerida} cm.
              <button
                type="button"
                className="boton boton--chico"
                style={{ marginTop: 8 }}
                onClick={() => {
                  setAltura(String(a.alturaSugerida));
                  void a.guardar({ altura_cm: a.alturaSugerida }, 'altura');
                }}
              >
                Usar {a.alturaSugerida} cm
              </button>
            </Aviso>
          )}

          <div>
            <Selector
              etiqueta="Sexo"
              value={p.sexo}
              onChange={(e) => void a.guardar({ sexo: e.target.value as Sexo }, 'sexo')}
            >
              <option value="M">Masculino</option>
              <option value="F">Femenino</option>
              <option value="otro">Prefiero no decirlo</option>
            </Selector>
            <MarcaGuardado visible={a.guardado === 'sexo'} />
          </div>

          <div>
            <Selector
              etiqueta="Unidad preferida"
              value={p.unidad_preferida}
              onChange={(e) => void a.guardar({ unidad_preferida: e.target.value as Unidad }, 'unidad')}
            >
              <option value="kg">Kilos</option>
              <option value="lb">Libras</option>
            </Selector>
            <p className="nota--fina" style={{ marginTop: 4 }}>
              Las series se muestran como las leíste en la máquina (esa unidad no se convierte).
              Esto cambia el peso corporal y los totales.
            </p>
            <MarcaGuardado visible={a.guardado === 'unidad'} />
          </div>

          <div>
            <Selector
              etiqueta="Fórmula de e1RM"
              value={p.formula_e1rm}
              onChange={(e) => void a.guardar({ formula_e1rm: e.target.value as 'epley' | 'brzycki' }, 'formula')}
            >
              <option value="epley">Epley</option>
              <option value="brzycki">Brzycki</option>
            </Selector>
            <MarcaGuardado visible={a.guardado === 'formula'} />
          </div>
        </div>

        {a.error && (
          <div style={{ marginTop: 'var(--e3)' }}>
            <Aviso tono="importante">{a.error}</Aviso>
          </div>
        )}
      </section>

      <SeccionRutina refSeccion={refRutina} anclada={detalle === 'rutina'} />

      <SeccionObjetivos a={a} refSeccion={refObjetivos} />

      <section className="tarjeta">
        <span className="eyebrow">Interfaz</span>
        <p className="nota" style={{ marginTop: 4 }}>
          Estás viendo la versión <strong>{variante === 'mobile' ? 'de celular' : 'de escritorio'}</strong>. En
          automático se elige por el ancho de pantalla y el tipo de puntero; ahora detecta{' '}
          {detectada === 'mobile' ? 'celular' : 'escritorio'}.
        </p>

        <div className="fila" style={{ gap: 'var(--e2)', marginTop: 'var(--e3)' }}>
          {(['auto', 'mobile', 'desktop'] as const).map((v) => (
            <button
              key={v}
              type="button"
              className={preferencia === v ? 'boton boton--chico boton--principal crece' : 'boton boton--chico crece'}
              onClick={() => fijar(v)}
            >
              {v === 'auto' ? 'Automático' : v === 'mobile' ? 'Celular' : 'Escritorio'}
            </button>
          ))}
        </div>
      </section>

      <section className="tarjeta">
        <span className="eyebrow">Tema</span>
        <div className="fila" style={{ gap: 'var(--e2)', marginTop: 'var(--e3)' }}>
          <button
            type="button"
            className={a.tema === 'oscuro' ? 'boton boton--chico boton--principal crece' : 'boton boton--chico crece'}
            onClick={() => a.setTema('oscuro')}
          >
            Oscuro
          </button>
          <button
            type="button"
            className={a.tema === 'claro' ? 'boton boton--chico boton--principal crece' : 'boton boton--chico crece'}
            onClick={() => a.setTema('claro')}
          >
            Claro
          </button>
        </div>
        <p className="nota--fina" style={{ marginTop: 'var(--e2)' }}>
          El oscuro es el default porque el caso de uso es un gimnasio con poca luz.
        </p>
      </section>

      <section className="tarjeta">
        <span className="eyebrow">Sincronización</span>
        <p className="nota" style={{ marginTop: 4 }}>
          {sync.enLinea ? 'Con conexión.' : 'Sin conexión.'}{' '}
          {sync.pendientes > 0
            ? `${sync.pendientes} ${sync.pendientes === 1 ? 'cambio' : 'cambios'} por subir.`
            : 'Todo subido.'}
        </p>

        {sync.conError.length > 0 && (
          <div style={{ marginTop: 'var(--e3)' }}>
            <Aviso tono="importante" titulo="El servidor rechazó algunos datos">
              {sync.conError.slice(0, 3).map((s) => (
                <span key={s.id} style={{ display: 'block' }}>
                  {s.tabla}: {s.ultimoError}
                </span>
              ))}
              Quedan guardados en el teléfono para que los puedas corregir.
            </Aviso>
          </div>
        )}

        <button
          type="button"
          className="boton"
          style={{ marginTop: 'var(--e3)' }}
          onClick={() => void sync.sincronizarAhora()}
          disabled={sync.sincronizando}
        >
          <Icono nombre="nube" tamano={18} />
          {sync.sincronizando ? 'Sincronizando…' : 'Sincronizar ahora'}
        </button>
      </section>

      <section className="tarjeta">
        <span className="eyebrow">Tus datos</span>
        <p className="nota" style={{ marginTop: 4 }}>
          Son tuyos: podés bajarlos completos cuando quieras.
        </p>
        <button type="button" className="boton" style={{ marginTop: 'var(--e3)' }} onClick={a.exportarTodoJSON}>
          Exportar todo (JSON)
        </button>
        <ExportarCSV a={a} />
      </section>

      <SeccionAyuda />

      <FormularioClave />

      <SalirDeLaCuenta a={a} onSalir={onSalir} centrado />
    </>
  );
}

// ---------------------------------------------------------------------------
// Rutina y plan — MOBILE
// ---------------------------------------------------------------------------

function SeccionRutina({
  refSeccion,
  anclada,
}: {
  refSeccion: RefObject<HTMLElement | null>;
  anclada: boolean;
}): ReactNode {
  const r = useRutina();
  const [abierta, setAbierta] = useState(anclada);

  useEffect(() => {
    if (anclada) setAbierta(true);
  }, [anclada]);

  const resumenRutina =
    r.dias.length === 0
      ? 'Todavía no hay días cargados.'
      : `${r.dias.length} ${r.dias.length === 1 ? 'día' : 'días'} · ${r.totalEjercicios} ${
          r.totalEjercicios === 1 ? 'ejercicio' : 'ejercicios'
        }`;

  return (
    <section className="tarjeta" ref={refSeccion}>
      <div className="fila fila--entre">
        <span className="eyebrow">Rutina y plan</span>
        <button
          type="button"
          className="boton boton--chico boton--fantasma"
          onClick={() => setAbierta(!abierta)}
          aria-expanded={abierta}
        >
          {abierta ? 'Cerrar' : 'Abrir'}
        </button>
      </div>
      <p className="nota" style={{ marginTop: 4 }}>
        {r.rutina?.nombre ?? 'Sin rutina'} · {resumenRutina}
        {r.plan ? ` · paquete de ${r.plan.sesiones_totales}, vence el ${fechaLarga(r.plan.fecha_vencimiento)}` : ' · sin paquete'}
      </p>

      {abierta && (
        <>
          <hr className="separador" />
          <span className="eyebrow etiqueta">Los días de tu rutina</span>

          {r.dias.length === 0 ? (
            <Vacio>Tu rutina no tiene días. Agregá el primero y ponele ejercicios.</Vacio>
          ) : (
            <div className="columna" style={{ gap: 'var(--e3)' }}>
              {r.dias.map((item) => (
                <DiaEditable key={item.dia.id} r={r} item={item} />
              ))}
            </div>
          )}

          <button
            type="button"
            className="boton boton--fantasma"
            style={{ marginTop: 'var(--e3)', width: '100%' }}
            onClick={() => void r.agregarDia()}
          >
            <Icono nombre="mas" tamano={16} />
            Agregar un día
          </button>
          <MarcaGuardado visible={r.marca === 'dias'} />

          {r.errorRutina && <Aviso tono="importante">{r.errorRutina}</Aviso>}

          <p className="nota--fina" style={{ marginTop: 'var(--e3)' }}>
            Cada ejercicio entra con 3 series de 10 a 12 reps, y el peso lo ponés en la sesión.
            Cambiar la rutina no toca nada de lo que ya registraste. Todavía no se reordena: para
            cambiar el orden hay que quitar y volver a agregar.
          </p>
          <p className="nota--fina" style={{ marginTop: 4 }}>
            Quitar saca la fila de este dispositivo. La planilla todavía no borra filas, así que lo
            que ya se subió puede volver en la próxima sincronización; ahí se borra a mano.
          </p>

          <hr className="separador" />
          <BloqueGimnasio r={r} />

          <hr className="separador" />
          <BloquePaquete r={r} />
        </>
      )}
    </section>
  );
}

function DiaEditable({ r, item }: { r: ReturnType<typeof useRutina>; item: DiaDeRutina }): ReactNode {
  const [agregando, setAgregando] = useState(false);
  const [confirmando, setConfirmando] = useState(false);

  return (
    <div className="tarjeta tarjeta--plana" style={{ border: '1px solid var(--hierro-borde)', padding: 'var(--e3)' }}>
      <input
        className="campo campo--texto"
        style={{ minHeight: 44, fontWeight: 600 }}
        defaultValue={item.dia.nombre}
        onBlur={(e) => void r.renombrarDia(item.dia, e.target.value)}
        aria-label={`Nombre del día «${item.dia.nombre}»`}
      />
      <MarcaGuardado visible={r.marca === `dia:${item.dia.id}`} />

      <div className="columna" style={{ gap: 4, marginTop: 'var(--e2)' }}>
        {item.filas.map(({ fila, ejercicio }) => (
          <div key={fila.id} className="fila fila--entre">
            <span style={{ fontSize: 14 }}>
              {ejercicio?.nombre ?? fila.ejercicio_id}
              {!ejercicio && (
                <span className="nota--fina" style={{ display: 'block', fontWeight: 400 }}>
                  no está en el catálogo de este gimnasio
                </span>
              )}
            </span>
            <button
              type="button"
              className="boton boton--chico boton--fantasma"
              style={{ minHeight: 44, padding: '0 12px' }}
              onClick={() => void r.quitarEjercicio(fila)}
              aria-label={`Quitar ${ejercicio?.nombre ?? fila.ejercicio_id} de ${item.dia.nombre}`}
            >
              <Icono nombre="cerrar" tamano={14} />
            </button>
          </div>
        ))}

        {item.filas.length === 0 && (
          <p className="nota--fina" style={{ color: 'var(--ambar)' }}>
            Este día no tiene ejercicios: la sesión abriría vacía.
          </p>
        )}
      </div>

      {/* Objetivos de dedo: 48 px, aunque sean botones secundarios. Se tocan con una mano. */}
      <div className="fila" style={{ gap: 'var(--e2)', marginTop: 'var(--e3)' }}>
        <button
          type="button"
          className="boton boton--chico boton--fantasma crece"
          style={{ minHeight: 48 }}
          onClick={() => setAgregando(true)}
        >
          <Icono nombre="mas" tamano={14} />
          Agregar ejercicio
        </button>
        <button
          type="button"
          className="boton boton--chico boton--fantasma"
          style={{ minHeight: 48 }}
          onClick={() => setConfirmando(true)}
        >
          Quitar el día
        </button>
      </div>

      {confirmando && (
        <div style={{ marginTop: 'var(--e3)' }}>
          <Aviso tono="atencion" titulo={`¿Quitar «${item.dia.nombre}»?`}>
            Sale de la rutina y deja de aparecer al abrir una sesión. Lo que ya registraste ese día
            no se toca: las series siguen en tu historial.
            <div className="fila" style={{ gap: 'var(--e2)', marginTop: 'var(--e3)' }}>
              <button
                type="button"
                className="boton boton--chico boton--peligro crece"
                style={{ minHeight: 48 }}
                onClick={() => {
                  setConfirmando(false);
                  void r.quitarDia(item.dia);
                }}
              >
                Sí, quitarlo
              </button>
              <button
                type="button"
                className="boton boton--chico crece"
                style={{ minHeight: 48 }}
                onClick={() => setConfirmando(false)}
              >
                Cancelar
              </button>
            </div>
          </Aviso>
        </div>
      )}

      {agregando && (
        <HojaInferior titulo={`Agregar a ${item.dia.nombre}`} onCerrar={() => setAgregando(false)}>
          <HojaCatalogo
            disponibles={r.disponiblesPara(item.dia.id)}
            onElegir={async (id) => {
              await r.agregarEjercicio(item.dia.id, id);
              setAgregando(false);
            }}
          />
        </HojaInferior>
      )}
    </div>
  );
}

/**
 * Elegir del catálogo con filtro de texto: son decenas de ejercicios y scrollear una lista de
 * treinta con una mano no es una opción. Es la misma hoja que usa Entrenar para agregar uno.
 */
function HojaCatalogo({
  disponibles,
  onElegir,
}: {
  disponibles: Ejercicio[];
  onElegir: (ejercicioId: string) => Promise<void>;
}): ReactNode {
  const [filtro, setFiltro] = useState('');
  const norma = (s: string): string => s.toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');

  const visibles = filtro.trim()
    ? disponibles.filter((e) => norma(e.nombre).includes(norma(filtro.trim())))
    : disponibles;

  return (
    <>
      <Campo
        etiqueta="Buscar"
        texto
        placeholder="Ej. press, curl, remo…"
        value={filtro}
        onChange={(e) => setFiltro(e.target.value)}
      />

      {visibles.length === 0 ? (
        <Vacio>
          {disponibles.length === 0
            ? 'Todos los ejercicios del catálogo ya están en este día.'
            : `Ningún ejercicio coincide con "${filtro.trim()}".`}
        </Vacio>
      ) : (
        <div className="columna" style={{ gap: 'var(--e2)' }}>
          {visibles.map((e) => (
            <button
              key={e.id}
              type="button"
              className="boton"
              onClick={() => void onElegir(e.id)}
              style={{ justifyContent: 'flex-start', paddingLeft: 'var(--e4)' }}
            >
              {e.nombre}
            </button>
          ))}
        </div>
      )}

      <p className="nota--fina">Entra con 3 series de 10 a 12 reps. Se cambia en cada sesión.</p>
    </>
  );
}

function BloqueGimnasio({ r }: { r: ReturnType<typeof useRutina> }): ReactNode {
  if (!r.gimnasio) {
    return (
      <>
        <span className="eyebrow etiqueta">Gimnasio</span>
        <Vacio>Todavía no hay un gimnasio cargado.</Vacio>
      </>
    );
  }

  return (
    <>
      <div className="fila fila--entre">
        <span className="eyebrow etiqueta" style={{ marginBottom: 0 }}>
          Gimnasio
        </span>
        <Insignia tono="agua">compartido</Insignia>
      </div>
      <p className="nota" style={{ marginTop: 4 }}>
        {r.gimnasio.nombre}
      </p>

      <span className="eyebrow etiqueta" style={{ marginTop: 'var(--e3)' }}>
        ¿Qué días cierra?
      </span>
      <div className="fila" style={{ gap: 6, flexWrap: 'wrap' }}>
        {r.diasSemana.map((d) => (
          <button
            key={d.n}
            type="button"
            className={r.diasCierre.includes(d.n) ? 'boton boton--chico boton--principal' : 'boton boton--chico'}
            style={{ flex: 1, minWidth: 48, minHeight: 48 }}
            aria-pressed={r.diasCierre.includes(d.n)}
            onClick={() => r.alternarCierre(d.n)}
          >
            {d.etiqueta}
          </button>
        ))}
      </div>
      <p className="nota--fina" style={{ marginTop: 6 }}>
        Estos días no cuentan como días disponibles para tu plan. Si no cierra ninguno, destildá
        todos.
      </p>

      <button
        type="button"
        className="boton"
        style={{ marginTop: 'var(--e3)', width: '100%' }}
        disabled={!r.hayCambiosGimnasio || r.subiendoGimnasio}
        onClick={() => void r.guardarGimnasio()}
      >
        {r.subiendoGimnasio ? 'Subiendo…' : 'Guardar los días de cierre'}
      </button>
      {!r.hayCambiosGimnasio && !r.subiendoGimnasio && (
        <p className="nota--fina" style={{ marginTop: 4 }}>
          Tocá un día para cambiarlo y el botón se habilita.
        </p>
      )}
      <MarcaGuardado visible={r.marca === 'gimnasio'}>✓ Guardado y subido</MarcaGuardado>

      {r.errorGimnasio && (
        <div style={{ marginTop: 'var(--e3)' }}>
          <Aviso tono="importante" titulo="No se pudo subir">
            {r.errorGimnasio} El gimnasio lo comparten todos los que entrenan ahí, así que el cambio
            necesita conexión: no se guarda a medias.
            <button
              type="button"
              className="boton boton--chico"
              style={{ marginTop: 8 }}
              onClick={() => void r.guardarGimnasio()}
              disabled={r.subiendoGimnasio}
            >
              Reintentar
            </button>
          </Aviso>
        </div>
      )}
    </>
  );
}

function BloquePaquete({ r }: { r: ReturnType<typeof useRutina> }): ReactNode {
  const { ir } = useRuta();

  return (
    <>
      <span className="eyebrow etiqueta">Paquete de sesiones</span>
      <p className="nota" style={{ marginTop: 4 }}>
        {r.plan
          ? `Paquete de ${r.plan.sesiones_totales} sesiones, vence el ${fechaLarga(r.plan.fecha_vencimiento)}. Cuántas llevás usadas y cuánto colchón te queda lo dice Plan.`
          : 'No hay ningún paquete cargado. Sin paquete la app registra igual, pero no puede avisarte si te alcanza el tiempo.'}
      </p>
      {r.plan && (
        <button
          type="button"
          className="boton boton--chico boton--fantasma"
          style={{ marginTop: 'var(--e2)', minHeight: 48 }}
          onClick={() => ir('calendario')}
        >
          Ir a Plan
        </button>
      )}

      <div className="columna" style={{ gap: 'var(--e3)', marginTop: 'var(--e3)' }}>
        <Campo
          etiqueta="Sesiones del paquete"
          inputMode="numeric"
          value={r.sesionesTotales}
          onChange={(e) => r.setSesionesTotales(e.target.value)}
        />
        <Campo
          etiqueta="Vence el"
          texto
          type="date"
          value={r.vencimiento}
          min={r.datos.hoy}
          onChange={(e) => r.setVencimiento(e.target.value)}
        />
      </div>

      <button
        type="button"
        className="boton"
        style={{ marginTop: 'var(--e3)', width: '100%' }}
        disabled={!r.planValido}
        onClick={() => void r.guardarPaquete()}
      >
        {r.plan ? 'Guardar el paquete' : 'Cargar un paquete'}
      </button>
      {r.queFaltaPlan && (
        <p className="nota--fina" style={{ marginTop: 4 }}>
          {r.queFaltaPlan}
        </p>
      )}
      <MarcaGuardado visible={r.marca === 'plan'} />

      {r.errorPlan && (
        <div style={{ marginTop: 'var(--e3)' }}>
          <Aviso tono="importante">{r.errorPlan}</Aviso>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Objetivos — MOBILE
// ---------------------------------------------------------------------------

function SeccionObjetivos({
  a,
  refSeccion,
}: {
  a: ReturnType<typeof useAjustes>;
  refSeccion: RefObject<HTMLElement | null>;
}): ReactNode {
  const p = a.datos.perfil;
  if (!p) return null;

  return (
    <section className="tarjeta" ref={refSeccion}>
      <span className="eyebrow">Objetivos</span>

      <span className="eyebrow etiqueta" style={{ marginTop: 'var(--e3)' }}>
        Series semanales por grupo
      </span>
      <div className="columna" style={{ gap: 'var(--e2)' }}>
        {a.grupos.map((g) => (
          <div key={g} className="fila fila--entre">
            <span style={{ textTransform: 'capitalize' }}>
              {g}
              <MarcaGuardado visible={a.guardado === `grupo:${g}`} />
            </span>
            <input
              className="campo"
              style={{ width: 84, minHeight: 44, textAlign: 'center' }}
              inputMode="numeric"
              placeholder="—"
              defaultValue={p.objetivo_series_semanales[g] ?? ''}
              onBlur={(e) => {
                const n = Number(e.target.value);
                void a.setObjetivo(g, Number.isFinite(n) && n > 0 ? n : null);
              }}
              aria-label={`Objetivo semanal de ${g}`}
            />
          </div>
        ))}
      </div>

      <hr className="separador" />

      <span className="eyebrow etiqueta">Comida</span>
      <div className="columna" style={{ gap: 'var(--e3)' }}>
        <div>
          <Campo
            etiqueta="Calorías por día (kcal)"
            inputMode="numeric"
            placeholder="—"
            defaultValue={p.objetivo_kcal ?? ''}
            // El campo se deja en lo que quedó guardado: si el texto no era un número, mostrarlo
            // igual haría creer que hay un objetivo que no existe.
            onBlur={(e) => {
              const valor = objetivoOpcional(e.target.value);
              e.target.value = valor === null ? '' : String(valor);
              void a.guardar({ objetivo_kcal: valor }, 'kcal');
            }}
          />
          <MarcaGuardado visible={a.guardado === 'kcal'} />
        </div>
        <div>
          <Campo
            etiqueta="Proteína por día (g)"
            inputMode="numeric"
            placeholder="—"
            defaultValue={p.objetivo_proteina_g ?? ''}
            onBlur={(e) => {
              const valor = objetivoOpcional(e.target.value);
              e.target.value = valor === null ? '' : String(valor);
              void a.guardar({ objetivo_proteina_g: valor }, 'proteina');
            }}
          />
          <MarcaGuardado visible={a.guardado === 'proteina'} />
        </div>
      </div>
      <p className="nota--fina" style={{ marginTop: 'var(--e2)' }}>
        La app no sugiere un número: este objetivo es tuyo. Vacío = sin objetivo.
      </p>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Piezas compartidas con la variante desktop
// ---------------------------------------------------------------------------

/** La confirmación efímera de §3: donde tocaste, al instante, y se apaga sola. */
export function MarcaGuardado({
  visible = true,
  children = '✓ Guardado',
}: {
  visible?: boolean;
  children?: ReactNode;
}): ReactNode {
  if (!visible) return null;
  return (
    <span className="nota--fina" role="status" style={{ display: 'block', marginTop: 4, color: 'var(--agua)' }}>
      {children}
    </span>
  );
}

/**
 * Salir borra la base local, así que "siguen en tu planilla" sólo es cierto si la cola está
 * vacía. Con cambios sin subir se dice cuántos son y se ofrece subirlos primero.
 */
export function SalirDeLaCuenta({
  a,
  onSalir,
  centrado = false,
}: {
  a: ReturnType<typeof useAjustes>;
  onSalir: () => Promise<void>;
  centrado?: boolean;
}): ReactNode {
  if (a.salida) {
    return (
      <Aviso tono="importante" titulo="Antes de salir">
        Hay {a.salida.pendientes} {a.salida.pendientes === 1 ? 'cambio' : 'cambios'} que todavía no
        {a.salida.pendientes === 1 ? ' subió' : ' subieron'} y se van a perder.
        {a.errorSalida && <span style={{ display: 'block', marginTop: 8 }}>{a.errorSalida}</span>}
        <div className="columna" style={{ gap: 'var(--e2)', marginTop: 'var(--e3)' }}>
          <button
            type="button"
            className="boton boton--principal"
            disabled={a.subiendo}
            onClick={() => void a.subirYSalir(onSalir)}
          >
            {a.subiendo ? 'Subiendo…' : 'Subir y salir'}
          </button>
          <button
            type="button"
            className="boton boton--peligro"
            disabled={a.subiendo}
            onClick={() => void onSalir()}
          >
            Salir igual
          </button>
          <button type="button" className="boton boton--fantasma" onClick={a.cancelarSalida}>
            Quedarme
          </button>
        </div>
      </Aviso>
    );
  }

  return (
    <>
      <button type="button" className="boton boton--peligro" onClick={() => void a.intentarSalir(onSalir)}>
        Salir de la cuenta
      </button>
      <p className="nota--fina" style={centrado ? { textAlign: 'center' } : { marginTop: 'var(--e2)' }}>
        Al salir se borran los datos de este dispositivo. Siguen en tu planilla.
      </p>
    </>
  );
}

/** Compartido con la variante desktop. */
export function SeccionAyuda(): ReactNode {
  const [abierta, setAbierta] = useState(false);
  const [reiniciadas, setReiniciadas] = useState(false);

  return (
    <section className="tarjeta">
      <div className="fila fila--entre">
        <span className="eyebrow">Cómo funciona</span>
        <button type="button" className="boton boton--chico boton--fantasma" onClick={() => setAbierta(!abierta)}>
          {abierta ? 'Cerrar' : 'Abrir'}
        </button>
      </div>

      {abierta && (
        <div style={{ marginTop: 'var(--e4)' }}>
          <PanelDeAyuda compacto />

          <hr className="separador" />

          <button
            type="button"
            className="boton boton--chico boton--fantasma"
            onClick={() => {
              reiniciarPistas();
              setReiniciadas(true);
            }}
          >
            Volver a mostrar las pistas
          </button>
          {reiniciadas && (
            <p className="nota--fina" style={{ marginTop: 6 }}>
              Listo. Las pistas vuelven a aparecer al recargar.
            </p>
          )}
        </div>
      )}
    </section>
  );
}

/** Compartido con la variante desktop: el formulario es el mismo, sólo cambia dónde va. */
export function FormularioClave(): ReactNode {
  const c = useClave();
  const falta = c.queFalta();

  return (
    <section className="tarjeta">
      <span className="eyebrow">Cambiar la clave</span>

      <div className="columna" style={{ gap: 'var(--e3)', marginTop: 'var(--e3)' }}>
        <Campo
          etiqueta="Clave actual"
          texto
          type="password"
          autoComplete="current-password"
          value={c.actual}
          onChange={(e) => c.setActual(e.target.value)}
        />
        <Campo
          etiqueta="Clave nueva"
          texto
          type="password"
          autoComplete="new-password"
          value={c.nueva}
          onChange={(e) => c.setNueva(e.target.value)}
          ayuda={`Mínimo ${MINIMO_CLAVE} caracteres.`}
        />
        <Campo
          etiqueta="Repetí la clave nueva"
          texto
          type="password"
          autoComplete="new-password"
          value={c.repetida}
          onChange={(e) => c.setRepetida(e.target.value)}
        />

        {c.error && <Aviso tono="importante">{c.error}</Aviso>}
        {c.listo && <Aviso tono="info">Clave cambiada. Usala la próxima vez que entres.</Aviso>}

        <button type="button" className="boton" disabled={!c.valido || c.enviando} onClick={() => void c.cambiar()}>
          {c.enviando ? 'Cambiando…' : 'Cambiar la clave'}
        </button>

        {/* El botón deshabilitado sin explicación es una pared: se dice qué falta. */}
        {falta && (c.actual.length > 0 || c.nueva.length > 0) && <p className="nota--fina">{falta}</p>}

        <p className="nota--fina">
          Las sesiones ya abiertas en otros dispositivos siguen funcionando: los tokens están
          firmados y no hay lista de revocación. Si perdiste un dispositivo, salí de la cuenta ahí.
        </p>
      </div>
    </section>
  );
}

export function ExportarCSV({ a }: { a: ReturnType<typeof useAjustes> }): ReactNode {
  const [tabla, setTabla] = useState('series.csv');
  const archivo = a.archivosCSV.find((x) => x.nombre === tabla);

  return (
    <div style={{ marginTop: 'var(--e3)' }}>
      <Selector etiqueta="Exportar una tabla en CSV" value={tabla} onChange={(e) => setTabla(e.target.value)}>
        {a.archivosCSV.map((x) => (
          <option key={x.nombre} value={x.nombre}>
            {x.nombre.replace('.csv', '')}
          </option>
        ))}
      </Selector>
      <button
        type="button"
        className="boton boton--chico"
        style={{ marginTop: 'var(--e2)' }}
        disabled={!archivo}
        onClick={() => archivo && a.descargar(archivo)}
      >
        Descargar {tabla}
      </button>
      <p className="nota--fina" style={{ marginTop: 4 }}>
        El CSV usa punto decimal para que se pueda abrir en cualquier idioma. Los pesos van tal como
        los registraste, con su unidad al lado; nada convertido.
      </p>
    </div>
  );
}
